/**
 * Bildirim servisi — ORTAK ALTYAPI (Parça C).
 *
 * KARAR #22: Bu sürümde SADECE `sistemIci` kanal yazılır.
 *            Telegram / WhatsApp / PWA-push EN SONA bırakıldı (KALAN #3).
 *            Telefon titremez; bildirim yalnız uygulama içinde görünür.
 *
 * Tasarım: avans/izin servisleriyle aynı stil — undefined alan yazılmaz,
 * where+orderBy yerine client-side sort (yeni Firestore index'i gerekmesin).
 *
 * Tip genişletme: resmi `BildirimTipi`'ye görev akışı için 2 tip eklendi
 * (`gorevReddedildi`, `gorevGeriAlindi`). gorev-service'teki GorevEk /
 * mesaiDisiDahil paterniyle aynı bilinçli genişletme (KARAR #18 / #23).
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where,
  writeBatch,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { Bildirim, BildirimTipi, BildirimAciliyet } from '@/types'

const BILDIRIMLER = 'bildirimler'

/** Resmi şemaya eklenen görev bildirim tipleri (KARAR #23). */
export type BildirimTipiEk = 'gorevReddedildi' | 'gorevGeriAlindi' | 'extraMesaiOnay' | 'maasHareketOnay'
export type BildirimTipiGenis = BildirimTipi | BildirimTipiEk

export interface BildirimGonderData {
  kullaniciId: string
  tipi: BildirimTipiGenis
  baslik: string
  mesaj: string
  aciliyet?: BildirimAciliyet
  link?: string
  ilgiliKayit?: { koleksiyon: string; id: string }
}

/**
 * Tek bir kullanıcıya sistem-içi bildirim yazar.
 * Hata fırlatmaz değil — çağıran taraf isterse try/catch ile sarar
 * (görev akışını bildirim hatası bozmasın diye gorev-service sarıyor).
 */
export async function bildirimGonder(d: BildirimGonderData): Promise<string> {
  const db = getFirestoreDb()
  const id = doc(collection(db, BILDIRIMLER)).id

  const docData: Record<string, unknown> = {
    kullaniciId: d.kullaniciId,
    tipi: d.tipi,
    aciliyet: d.aciliyet ?? 'normal',
    baslik: d.baslik,
    mesaj: d.mesaj,
    gonderilenKanallar: {
      sistemIci: { gonderildi: true, tarih: nowServer() },
    },
    okundu: false,
    olusturmaTarihi: nowServer(),
  }
  if (d.link) docData.link = d.link
  if (d.ilgiliKayit) docData.ilgiliKayit = d.ilgiliKayit

  await setDoc(doc(db, BILDIRIMLER, id), docData)
  return id
}

/**
 * Birden çok kullanıcıya aynı bildirimi yazar (batch).
 * Aynı kullanıcı birden çok kez verilirse tekilleştirilir.
 */
export async function cokluBildirimGonder(
  kullaniciIdleri: string[],
  ortak: Omit<BildirimGonderData, 'kullaniciId'>
): Promise<void> {
  const hedefler = Array.from(new Set(kullaniciIdleri.filter(Boolean)))
  if (hedefler.length === 0) return

  const db = getFirestoreDb()
  const batch = writeBatch(db)
  for (const uid of hedefler) {
    const id = doc(collection(db, BILDIRIMLER)).id
    const docData: Record<string, unknown> = {
      kullaniciId: uid,
      tipi: ortak.tipi,
      aciliyet: ortak.aciliyet ?? 'normal',
      baslik: ortak.baslik,
      mesaj: ortak.mesaj,
      gonderilenKanallar: {
        sistemIci: { gonderildi: true, tarih: nowServer() },
      },
      okundu: false,
      olusturmaTarihi: nowServer(),
    }
    if (ortak.link) docData.link = ortak.link
    if (ortak.ilgiliKayit) docData.ilgiliKayit = ortak.ilgiliKayit
    batch.set(doc(db, BILDIRIMLER, id), docData)
  }
  await batch.commit()
}

/** Bir kullanıcının bildirimleri (yeni → eski). */
export async function kullaniciBildirimleri(
  kullaniciId: string,
  max = 50
): Promise<Bildirim[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, BILDIRIMLER), where('kullaniciId', '==', kullaniciId))
  const snap = await getDocs(q)
  return snap.docs
    .map((s) => ({ id: s.id, ...s.data() }) as Bildirim)
    .sort(
      (a, b) =>
        (b.olusturmaTarihi?.toMillis?.() ?? 0) -
        (a.olusturmaTarihi?.toMillis?.() ?? 0)
    )
    .slice(0, max)
}

/** Okunmamış bildirim sayısı (rozet için). */
export async function okunmamisSayisi(kullaniciId: string): Promise<number> {
  const db = getFirestoreDb()
  const q = query(collection(db, BILDIRIMLER), where('kullaniciId', '==', kullaniciId))
  const snap = await getDocs(q)
  return snap.docs.filter((s) => (s.data() as Bildirim).okundu === false).length
}

export async function bildirimOkundu(id: string): Promise<void> {
  const db = getFirestoreDb()
  const ref = doc(db, BILDIRIMLER, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) return
  if ((snap.data() as Bildirim).okundu === true) return
  await updateDoc(ref, { okundu: true, okunmaTarihi: nowServer() })
}

/** Bir kullanıcının tüm okunmamışlarını okundu yapar (batch). */
export async function tumBildirimleriOkunduYap(kullaniciId: string): Promise<void> {
  const db = getFirestoreDb()
  const q = query(collection(db, BILDIRIMLER), where('kullaniciId', '==', kullaniciId))
  const snap = await getDocs(q)
  const okunmamis = snap.docs.filter((s) => (s.data() as Bildirim).okundu === false)
  if (okunmamis.length === 0) return
  const batch = writeBatch(db)
  for (const s of okunmamis) {
    batch.update(s.ref, { okundu: true, okunmaTarihi: nowServer() })
  }
  await batch.commit()
}
